import {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import logo from "../asserts/Logo_without_bg.png";

import {
  IconArrowUpRight,
  IconMapPin,
  IconMail,
  IconPhone,
  IconBrandInstagram,
  IconBrandFacebook,
  IconBrandLinkedin,
  IconSparkles,
  IconX,
  IconBook2,
  IconCode,
  IconChevronRight,
} from "@tabler/icons-react";


/* =========================================================
   ANIMATIONS
========================================================= */

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30,
  },

  visible: {
    opacity: 1,
    y: 0,

    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};


const stagger = {
  hidden: {},

  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};


/* =========================================================
   INSTAGRAM ACCOUNTS
========================================================= */

const instagramAccounts = [
  {
    id: "institute",

    title: "WE3X Institute",

    username: "@we3x_institute",

    description:
      "Courses, student activities, training, internships and career updates.",

    url: "https://www.instagram.com/we3x_institute/",

    icon: IconBook2,
  },

  {
    id: "technology",

    title: "WE3X Technologies",

    username: "@we3x_tech",

    description:
      "Software, websites, apps, digital products and business solutions.",

    url: "https://www.instagram.com/we3x_tech/",

    icon: IconCode,
  },
];


/* =========================================================
   FOOTER
========================================================= */

export default function Footer() {
  const location = useLocation();

  const isContactPage = location.pathname === "/contact";

  /* =========================================================
     FOOTER LINKS
  ========================================================= */

  const instituteLinks = [
    {
      name: "IT Training Institute",
      link: "/institute",
    },

    {
      name: "MERN Stack Training",
      link: "/institute/mern-stack",
    },
    {
      name: "Digital Marketing Training",
      link: "/institute/digital-marketing",
    },
    {
      name: "UI/UX Training",
      link: "/institute/ui-ux",
    },
    {
      name: "Python full stack Training",
      link: "/institute/python-full-stack",
    },
    {
      name: "Tally prime Training",
      link: "/institute/tally-prime",
    },
    {
      name: "Basic computers Training",
      link: "/institute/basic-computers",
    },
    {
      name: "Cyber Security Training",
      link: "/institute/cyber-security",
    },

    {
      name: "Career & Placement Support",
      link: "/institute",
    },
  ];


  const technologyLinks = [
    {
      name: "Web Applications",
      link: "/technology/web-applications",
    },

    {
      name: "Landing Pages & Websites",
      link: "/technology/landing-pages",
    },

    {
      name: "Digital Marketing Solutions",
      link: "/technology/digital-marketing",
    },

    {
      name: "Mobile Apps & Software Development",
      link: "/technology/mobile-apps",
    },
    {
      name: "Animated Websites",
      link: "/technology/animated-websites",
    },
    {
      name: "Video Editing & Motion Graphics",
      link: "/technology/video-editing",
    },
  ];


  const blogLinks = [
    {
      name: "Institute Career Blog",
      link: "/blog/which-course-is-best-for-it-jobs-in-2027",
    },

    {
      name: "Technology & Software Blog",
      link: "/blog/how-to-choose-the-right-software-company-for-your-business",
    },

    {
      name: "Digital Marketing Blog",
      link: "/blog/what-are-the-benefits-of-a-digital-marketing-course",
    },

    {
      name: "Local Web Development Benefits",
      link: "/blog/6-benefits-of-choosing-a-local-web-development-company",
    },
  ];


  return (
    <footer
      className="
        relative
        overflow-hidden
        bg-[#f0f0f1]
        text-black
      "
    >

      {/* =====================================================
          BACKGROUND GLOWS
      ===================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-40
          -top-40
          h-[400px]
          w-[400px]
          rounded-full
          bg-blue-200
          blur-[120px]
        "
      />


      <div
        className="
          pointer-events-none
          absolute
          -bottom-40
          right-0
          h-[400px]
          w-[400px]
          rounded-full
          bg-blue-200
          blur-[120px]
        "
      />



      {/* =====================================================
          TOP CTA
      ===================================================== */}

      <div
        className="
          relative
          border-b
          border-black/[0.06]
        "
      >

        <div
          className="
            mx-auto
            max-w-7xl
            px-5
            py-14
            sm:px-8
            sm:py-16
            lg:px-10
          "
        >

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{
              once: true,
              amount: 0.3,
            }}
            className="
              flex
              flex-col
              gap-8
              lg:flex-row
              lg:items-center
              lg:justify-between
            "
          >

            {/* CONTENT */}

            <div
              className="
                max-w-2xl
              "
            >

              <div
                className="
                  mb-4
                  flex
                  items-center
                  gap-2
                  text-xs
                  font-bold
                  uppercase
                  tracking-[0.2em]
                  text-neutral-700
                "
              >

                <IconSparkles
                  size={16}
                />

                Build Your Next Step

              </div>


              <h2
                className="
                  merriweather
                  text-3xl
                  font-black
                  tracking-[-0.03em]
                  sm:text-4xl
                  lg:text-[44px]
                  lg:leading-[1.1]
                "
              >

                Learn. Build.

                <br
                  className="
                    hidden
                    sm:block
                  "
                />

                Grow with WE3X.

              </h2>


              <p
                className="
                  mt-4
                  max-w-xl
                  text-sm
                  leading-7
                  text-neutral-600
                  sm:text-[15px]
                "
              >

                Build your career through practical IT training or grow
                your business with custom software, web development,
                SEO and digital marketing solutions.

              </p>

            </div>


            {/* CTA */}

            <Link
              to="/contact"
              className="
                group
                inline-flex
                w-fit
                items-center
                justify-center
                gap-3
                rounded-full
                bg-black
                px-7
                py-4
                text-sm
                font-bold
                text-white
                transition-all
                duration-300
                hover:-translate-y-1
                hover:bg-[#55aef3]
                hover:shadow-xl
                hover:shadow-blue-600/20
              "
            >

              Start With WE3X


              <IconArrowUpRight
                size={18}
                className="
                  transition-transform
                  duration-300
                  group-hover:translate-x-1
                  group-hover:-translate-y-1
                "
              />

            </Link>

          </motion.div>

        </div>

      </div>



      {/* =====================================================
          MAIN FOOTER
      ===================================================== */}

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.15,
        }}
        className="
          relative
          mx-auto
          grid
          max-w-7xl
          gap-12
          px-5
          py-16
          sm:px-8
          lg:grid-cols-[1.3fr_0.8fr_0.9fr_0.9fr]
          lg:px-10
          lg:py-20
        "
      >

        {/* =====================================================
            BRAND
        ===================================================== */}

        <motion.div
          variants={fadeUp}
          className="
            max-w-sm
          "
        >

          {/* LOGO */}

          <Link
            to="/"
            className="
              inline-flex
              items-center
              gap-3
            "
          >

            <img
              src={logo}
              alt="WE3X Institute and Technologies logo"
              className="
                h-auto
                max-w-[160px]
                object-contain
              "
            />

          </Link>


          {/* DESCRIPTION */}

          <p
            className="
              mt-6
              text-sm
              leading-7
              text-neutral-600
            "
          >

            WE3X is a technology and digital growth brand providing
            industry-focused IT training, custom software development,
            web and app development, SEO and digital marketing solutions
            in Karaikudi, Tamil Nadu.

          </p>



          {/* =====================================================
              LOCATION
          ===================================================== */}

          <a
            href="https://maps.app.goo.gl/ko38t3Zg5XTPRodw5"
            target="_blank"
            rel="noreferrer"
            className="
              group/location
              mt-7
              flex
              w-fit
              items-start
              gap-3
            "
          >

            <IconMapPin
              size={19}
              className="
                mt-1
                shrink-0
                text-[#55aef3]
                transition-transform
                group-hover/location:-translate-y-1
              "
            />


            <div>

              <p
                className="
                  text-sm
                  font-semibold
                  text-neutral-800
                "
              >
                Karaikudi, Tamil Nadu
              </p>


              <p
                className="
                  mt-1
                  text-xs
                  leading-5
                  text-neutral-500
                "
              >
                Serving students, startups and businesses.
              </p>

            </div>

          </a>



          {/* =====================================================
              SOCIAL MEDIA
          ===================================================== */}

          <div
            className="
              relative
              mt-7
              flex
              items-center
              gap-2
            "
          >

            {/* INSTAGRAM WITH TWO ACCOUNTS */}

            <InstagramSelector />


            {/* FACEBOOK */}

            <SocialIcon
              href="https://www.facebook.com/WE3xTechnologies/"
              label="Facebook"
              icon={
                <IconBrandFacebook
                  size={18}
                />
              }
            />


            {/* LINKEDIN */}

            <SocialIcon
              href="https://www.linkedin.com/company/we3x/home/"
              label="LinkedIn"
              icon={
                <IconBrandLinkedin
                  size={18}
                />
              }
            />

          </div>

        </motion.div>



        {/* =====================================================
            INSTITUTE
        ===================================================== */}

        <FooterColumn
          title="WE3X Institute"
          links={instituteLinks}
        />



        {/* =====================================================
            TECHNOLOGY
        ===================================================== */}

        <FooterColumn
          title="Technology"
          links={technologyLinks}
        />



        {/* =====================================================
            DIGITAL MARKETING
        ===================================================== */}

        <FooterColumn
          title="Blogs"
          links={blogLinks}
        />

      </motion.div>



      {/* =====================================================
          CONTACT STRIP
      ===================================================== */}

      <div
        className="
          relative
          border-t
          border-black/[0.06]
        "
      >

        <div
          className="
            mx-auto
            flex
            max-w-7xl
            flex-col
            gap-5
            px-5
            py-7
            sm:px-8
            md:flex-row
            md:items-center
            md:justify-between
            lg:px-10
          "
        >

          {/* CONTACT DETAILS */}

          <div
            className="
              flex
              flex-col
              gap-4
              sm:flex-row
              sm:items-center
              sm:gap-7
            "
          >

            {/* EMAIL */}

            <a
              href="mailto:connect@we3x.in"
              className="
                flex
                items-center
                gap-2
                text-xs
                font-medium
                text-neutral-500
                transition
                hover:text-black
              "
            >

              <IconMail
                size={16}
                className="
                  text-[#55aef3]
                "
              />

              connect@we3x.in

            </a>


            {/* PHONE */}

            <a
              href="tel:+919003408669"
              className="
                flex
                items-center
                gap-2
                text-xs
                font-medium
                text-neutral-500
                transition
                hover:text-black
              "
            >

              <IconPhone
                size={16}
                className="
                  text-[#55aef3]
                "
              />

              +91 90034 08669 | 88259 20268

            </a>

          </div>


          {/* CONTACT LINK — HIDDEN ON CONTACT PAGE */}

          {!isContactPage && (
            <Link
              to="/contact"
              className="
                group
                flex
                w-fit
                items-center
                gap-2
                text-xs
                font-bold
                text-neutral-800
              "
            >
              Contact WE3X

              <IconArrowUpRight
                size={15}
                className="
                  transition-transform
                  group-hover:translate-x-1
                  group-hover:-translate-y-1
                "
              />
            </Link>
          )}

        </div>

      </div>



      {/* =====================================================
          BOTTOM
      ===================================================== */}

      <div
        className="
          relative
          border-t
          border-black/[0.06]
          flex
          items-center
          justify-center
        "
      >

        <div
          className="
            mx-auto
            flex
            max-w-7xl
            flex-col
            gap-4
            px-5
            py-6
            text-xs
            text-neutral-500
            sm:px-8
            md:flex-row
            md:items-center
            md:justify-between
            lg:px-10
          "
        >

          <p>
            © {new Date().getFullYear()} WE3X. All rights reserved.
          </p>


          <div
            className="
              flex
              flex-wrap
              items-center
              gap-5
            "
          >
          </div>

        </div>

      </div>

    </footer>
  );
}



/* =========================================================
   FOOTER COLUMN
========================================================= */

function FooterColumn({
  title,
  links,
}) {

  return (
    <motion.div
      variants={fadeUp}
    >

      <h3
        className="
          mb-5
          text-sm
          font-bold
          text-neutral-900
        "
      >
        {title}
      </h3>


      <div
        className="
          flex
          flex-col
          gap-3
        "
      >

        {links.map(
          (item) => (

            <Link
              key={
                item.name
              }
              to={
                item.link
              }
              className="
                group
                flex
                w-fit
                items-center
                gap-1.5
                text-[13px]
                font-medium
                text-neutral-500
                transition
                duration-300
                hover:text-black
              "
            >

              {item.name}


              <IconArrowUpRight
                size={13}
                className="
                  opacity-0
                  transition-all
                  duration-300
                  group-hover:-translate-y-0.5
                  group-hover:translate-x-0.5
                  group-hover:opacity-100
                "
              />

            </Link>

          )
        )}

      </div>

    </motion.div>
  );
}



/* =========================================================
   INSTAGRAM SELECTOR
========================================================= */

function InstagramSelector() {

  const [
    open,
    setOpen,
  ] = useState(false);


  const containerRef =
    useRef(null);



  /* =========================================================
     CLOSE OUTSIDE / ESC
  ========================================================= */

  useEffect(() => {

    const handleOutsideClick = (
      event
    ) => {

      if (
        containerRef.current &&
        !containerRef.current.contains(
          event.target
        )
      ) {

        setOpen(false);

      }

    };


    const handleEscape = (
      event
    ) => {

      if (
        event.key ===
        "Escape"
      ) {

        setOpen(false);

      }

    };


    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );


    document.addEventListener(
      "keydown",
      handleEscape
    );


    return () => {

      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );


      document.removeEventListener(
        "keydown",
        handleEscape
      );

    };

  }, []);



  return (
    <div
      ref={
        containerRef
      }
      className="
        relative
        z-[100]
      "
    >

      {/* =====================================================
          INSTAGRAM BUTTON
      ===================================================== */}

      <motion.button
        type="button"
        onClick={() =>
          setOpen(
            (previous) =>
              !previous
          )
        }
        whileTap={{
          scale: 0.9,
        }}
        aria-label="Choose WE3X Instagram account"
        aria-expanded={
          open
        }
        className={`
          relative
          flex
          h-9
          w-9
          items-center
          justify-center
          rounded-full
          border
          transition-all
          duration-300

          ${
            open
              ? `
                -translate-y-1
                border-[#55aef3]
                bg-[#55aef3]
                text-white
                shadow-lg
                shadow-blue-500/20
              `
              : `
                border-black/[0.08]
                bg-white/40
                text-neutral-500
                hover:-translate-y-1
                hover:border-[#55aef3]/50
                hover:bg-white
                hover:text-[#55aef3]
              `
          }
        `}
      >

        <IconBrandInstagram
          size={18}
        />


        {/* OPEN DOT */}

        <AnimatePresence>

          {open && (

            <motion.span
              initial={{
                scale: 0,
              }}
              animate={{
                scale: 1,
              }}
              exit={{
                scale: 0,
              }}
              className="
                absolute
                -right-0.5
                -top-0.5
                h-2.5
                w-2.5
                rounded-full
                border-2
                border-[#f0f0f1]
                bg-green-500
              "
            />

          )}

        </AnimatePresence>

      </motion.button>



      {/* =====================================================
          INSTAGRAM ACCOUNT POPUP
      ===================================================== */}

      <AnimatePresence>

        {open && (

          <motion.div
            initial={{
              opacity: 0,
              y: 18,
              scale: 0.92,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: 10,
              scale: 0.95,
            }}
            transition={{
              type: "spring",
              stiffness: 330,
              damping: 26,
            }}
            className="
              absolute

              bottom-[calc(100%+16px)]
              left-0

              z-[999]

              w-[calc(100vw-40px)]
              max-w-[350px]

              overflow-hidden

              rounded-[22px]

              border
              border-black/[0.08]

              bg-white/95

              p-2

              text-black

              shadow-[0_30px_80px_rgba(0,0,0,0.16)]

              backdrop-blur-2xl

              sm:w-[350px]
            "
          >

            {/* =================================================
                POPUP HEADER
            ================================================= */}

            <div
              className="
                flex
                items-start
                justify-between
                gap-4
                px-3
                pb-4
                pt-3
              "
            >

              <div>

                {/* INSTAGRAM LABEL */}

                <div
                  className="
                    flex
                    items-center
                    gap-2
                  "
                >

                  <div
                    className="
                      flex
                      h-8
                      w-8
                      items-center
                      justify-center
                      rounded-[10px]
                      bg-[#55aef3]
                      text-white
                    "
                  >

                    <IconBrandInstagram
                      size={16}
                    />

                  </div>


                  <p
                    className="
                      text-[10px]
                      font-bold
                      uppercase
                      tracking-[0.18em]
                      text-[#3899e4]
                    "
                  >
                    Instagram
                  </p>

                </div>


                {/* TITLE */}

                <h4
                  className="
                    merriweather
                    mt-3
                    max-w-[250px]
                    text-[18px]
                    font-black
                    leading-[1.3]
                    tracking-[-0.025em]
                    text-neutral-950
                  "
                >
                  Which WE3X journey
                  do you want to follow?
                </h4>

              </div>


              {/* CLOSE */}

              <button
                type="button"
                onClick={() =>
                  setOpen(false)
                }
                aria-label="Close Instagram selector"
                className="
                  flex
                  h-8
                  w-8
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-neutral-100
                  text-neutral-400
                  transition-all
                  duration-300
                  hover:rotate-90
                  hover:bg-neutral-900
                  hover:text-white
                "
              >

                <IconX
                  size={15}
                />

              </button>

            </div>



            {/* =================================================
                ACCOUNTS
            ================================================= */}

            <div
              className="
                space-y-2
              "
            >

              {instagramAccounts.map(
                (
                  account,
                  index
                ) => {

                  const AccountIcon =
                    account.icon;


                  return (

                    <motion.a
                      key={
                        account.id
                      }
                      href={
                        account.url
                      }
                      target="_blank"
                      rel="noreferrer"
                      onClick={() =>
                        setOpen(false)
                      }
                      initial={{
                        opacity: 0,
                        x: -15,
                      }}
                      animate={{
                        opacity: 1,
                        x: 0,
                      }}
                      transition={{
                        delay:
                          0.08 +
                          index *
                            0.08,
                      }}
                      whileHover={{
                        x: 3,
                      }}
                      className="
                        group/account
                        relative
                        flex
                        items-center
                        gap-3
                        overflow-hidden
                        rounded-[16px]
                        border
                        border-neutral-100
                        bg-[#f8f8f8]
                        p-3.5
                        transition-colors
                        duration-300
                        hover:border-blue-100
                        hover:bg-blue-50
                      "
                    >

                      {/* ACCOUNT ICON */}

                      <div
                        className="
                          flex
                          h-11
                          w-11
                          shrink-0
                          items-center
                          justify-center
                          rounded-[13px]
                          bg-white
                          text-[#55aef3]
                          shadow-sm
                          transition-all
                          duration-300
                          group-hover/account:bg-[#55aef3]
                          group-hover/account:text-white
                        "
                      >

                        <AccountIcon
                          size={19}
                        />

                      </div>



                      {/* ACCOUNT CONTENT */}

                      <div
                        className="
                          min-w-0
                          flex-1
                        "
                      >

                        <div
                          className="
                            flex
                            items-center
                            gap-2
                          "
                        >

                          <p
                            className="
                              truncate
                              text-[13px]
                              font-black
                              text-neutral-900
                            "
                          >
                            {account.title}
                          </p>


                          <span
                            className="
                              h-1
                              w-1
                              shrink-0
                              rounded-full
                              bg-[#55aef3]
                            "
                          />

                        </div>


                        <p
                          className="
                            mt-0.5
                            truncate
                            text-[10px]
                            font-semibold
                            text-[#3899e4]
                          "
                        >
                          {account.username}
                        </p>


                        <p
                          className="
                            mt-1
                            line-clamp-2
                            text-[10px]
                            leading-4
                            text-neutral-400
                          "
                        >
                          {account.description}
                        </p>

                      </div>



                      {/* ARROW */}

                      <div
                        className="
                          flex
                          h-8
                          w-8
                          shrink-0
                          items-center
                          justify-center
                          rounded-full
                          text-neutral-300
                          transition-all
                          duration-300
                          group-hover/account:bg-white
                          group-hover/account:text-[#55aef3]
                        "
                      >

                        <IconChevronRight
                          size={16}
                        />

                      </div>



                      {/* HOVER LINE */}

                      <span
                        className="
                          absolute
                          bottom-0
                          left-0
                          h-[2px]
                          w-0
                          bg-[#55aef3]
                          transition-all
                          duration-500
                          group-hover/account:w-full
                        "
                      />

                    </motion.a>

                  );

                }
              )}

            </div>



            {/* =================================================
                POPUP FOOTER
            ================================================= */}

            <div
              className="
                flex
                items-center
                gap-2
                px-3
                pb-2
                pt-4
              "
            >

              <motion.span
                animate={{
                  scale: [
                    1,
                    1.3,
                    1,
                  ],

                  opacity: [
                    0.5,
                    1,
                    0.5,
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
                className="
                  h-1.5
                  w-1.5
                  rounded-full
                  bg-[#55aef3]
                "
              />


              <p
                className="
                  text-[9px]
                  font-semibold
                  text-neutral-400
                "
              >
                Choose the account that matters to you.
              </p>

            </div>



            {/* =================================================
                POPUP POINTER
            ================================================= */}

            <div
              className="
                absolute
                -bottom-[6px]
                left-[12px]
                h-3
                w-3
                rotate-45
                border-b
                border-r
                border-black/[0.06]
                bg-white
              "
            />

          </motion.div>

        )}

      </AnimatePresence>

    </div>
  );
}



/* =========================================================
   NORMAL SOCIAL ICON
========================================================= */

function SocialIcon({
  href,
  label,
  icon,
}) {

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="
        flex
        h-9
        w-9
        items-center
        justify-center
        rounded-full
        border
        border-black/[0.08]
        bg-white/40
        text-neutral-500
        transition-all
        duration-300
        hover:-translate-y-1
        hover:border-[#55aef3]/50
        hover:bg-white
        hover:text-[#55aef3]
      "
    >
      {icon}
    </a>
  );
}